import { Fragment, use } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Hymn, isMidiFileMissing, MessageType, SongSlug } from '../../../../../../common/src/model.ts';
import ErrorIcon from '../../../../assets/error.svg?react';
import NoSmokingIcon from '../../../../assets/no-smoking.svg?react';
import { testOrgIds } from '../../../../data/use_current_org.tsx';
import { HymnalsContext } from '../../../../data/use_hymnals.tsx';
import { useAllOrgs } from '../../../../data/use_orgs.tsx';
import {
  generateFileName,
  generateHymnUrl,
  getPdfUrlForHymn,
  getSongSlugParts,
  getSypUrlForHymn,
  isCustomSong,
} from '../../../../util/path.ts';
import { TestOrg } from './engineering_custom_music_page.tsx';

export interface HymnsByOrg {
  [orgId: string]: Hymn[];
}

export function EngineeringSongList({
  hymnsByOrg,
  isThisSunday,
  openPDFInBrowser,
  showPDFLink,
  showSypLink,
}: {
  hymnsByOrg: HymnsByOrg;
  isThisSunday?: (songSlug: SongSlug) => boolean;
  openPDFInBrowser?: boolean;
  showPDFLink?: (hymn: Hymn) => boolean;
  showSypLink?: boolean;
}) {
  const { orgs } = useAllOrgs();
  const hymnals = use(HymnalsContext);

  const getOrgName = (orgId: string) => {
    const org = orgs.find((org) => org.id === Number(orgId));
    if (org) {
      return testOrgIds.includes(org.id) ? <TestOrg>{org.name}</TestOrg> : org.name;
    }
  };

  const getOrgLocation = (orgId: string) => {
    return orgs.find((org) => org.id === Number(orgId))?.location;
  };

  const hymnCounts: { [songId: string]: number } = {};
  for (const [orgId, hymns] of Object.entries(hymnsByOrg)) {
    for (const hymn of hymns) {
      const songId = getSongId(orgId, hymn);
      hymnCounts[songId] = (hymnCounts[songId] ?? 0) + 1;
    }
  }
  const showHymnCounts = Object.values(hymnCounts).some((count) => count > 1);

  return (
    <SongListWrapper $showSypLink={showSypLink}>
      {!hymnsByOrg
        ? null
        : Object.entries(hymnsByOrg).map(([orgId, hymns]) =>
            hymns.map((hymn) => {
              const songId = getSongId(orgId, hymn);
              const hymnUrl = generateHymnUrl({ hymnals, hymn });
              if (!hymnUrl) {
                return null;
              }
              return (
                <Fragment key={songId}>
                  <IconWrapper key="icon">
                    {isMidiFileMissing(hymn) ? (
                      <NoSmokingIconWrapper />
                    ) : hymn.messageCounts?.[MessageType.Error] ? (
                      <ErrorIconWrapper />
                    ) : null}
                  </IconWrapper>
                  <TextNoWrap key="org-id">{getOrgName(orgId)}</TextNoWrap>
                  <TextNoWrap key="org-location">{getOrgLocation(orgId)}</TextNoWrap>
                  <SongId key="song-id">{showHymnCounts ? `${songId} (${hymnCounts[songId]})` : songId}</SongId>
                  <TextNoWrap>
                    <Link key="hymn-url" to={hymnUrl}>
                      {hymn.title}
                    </Link>
                  </TextNoWrap>
                  <PDFLink
                    key="pdf"
                    href={getPdfUrlForHymn(hymn)}
                    download={openPDFInBrowser ? undefined : `${orgId}-${generateFileName(hymn)}`}
                    $highlight={!showPDFLink || showPDFLink(hymn)}
                    target={openPDFInBrowser ? '_blank' : ''}
                  >
                    PDF {isThisSunday?.(hymn.slug) ? '(This Sunday)' : ''}
                  </PDFLink>
                  {showSypLink && (
                    <SypLink
                      key="syp"
                      href={getSypUrlForHymn(hymn)}
                      download={openPDFInBrowser ? undefined : `${orgId}-${generateFileName(hymn)}`}
                      target="_blank"
                    >
                      SYP
                    </SypLink>
                  )}
                </Fragment>
              );
            }),
          )}
    </SongListWrapper>
  );
}

const SongListWrapper = styled.div<{ $showSypLink?: boolean }>`
  display: grid;
  grid-template-columns: 17px 1fr 0.5fr 15ch 1fr 15ch ${(props) => (props.$showSypLink ? '10ch' : '')};
  justify-items: left;
`;

const SongId = styled.div`
  text-wrap: nowrap;
`;

export function getSongId(orgId: string, hymn: Hymn) {
  if (isCustomSong(hymn.slug)) {
    return `${orgId}-${hymn.songNumber}`;
  } else {
    const { hymnalSlugOrChurchId, songNumber } = getSongSlugParts(hymn.slug) ?? {};
    return `${hymnalSlugOrChurchId}-${songNumber}`;
  }
}

export const PDFLink = styled.a<{ $highlight: boolean }>`
  color: ${(props) => (props.$highlight ? 'var(--color-pdf-upload)' : 'var(--color-text)')};
`;

const SypLink = styled.a`
  color: var(--color-pdf-upload);
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const NoSmokingIconWrapper = styled(NoSmokingIcon)`
  width: 15px;
  height: 15px;
  fill: var(--color-dangerous-button);
`;

const ErrorIconWrapper = styled(ErrorIcon)`
  width: 20px;
  height: 20px;
  fill: var(--color-dangerous-button);
  margin-left: -1px;
`;

const TextNoWrap = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 100%;
  text-align: start;
  padding: 0 5px;
`;
